import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { withTranslation } from 'react-i18next';
import { useAPIError } from '../../../../context';
import {
    Disabler,
    H1Title,
    Introduction,
    ErrorBoundary,
    NoDataFoundCard,
    ToggleMode,
} from '../../../../components';
import { useMode } from '../../../../context/useMode';
import { MODES } from '../../../../utils/constants';
import {
    PartnershipCardSkeleton,
    PartnershipCardGrid,
    PartnershipCollapsibleCard,
} from '../../shared';
import { getPartnershipStatus } from '../../shared/partnershipsTools';
import { getAllPartnerships, getAllCharities } from '../../service';
import AllCharitiesPagination from './AllCharitiesPagination';
import CardMainButton from './CardMainButton';

const AllCharities = ({ t }) => {
    const { addError } = useAPIError();
    const { mode } = useMode();

    // we create a ref to detect if component is mounted
    const isMounted = useRef(false);

    const [partnershipsData, setPartnershipsData] = useState();
    const [isLoading, setIsLoading] = useState(true);
    const [isListLoading, setIsListLoading] = useState(true);
    const [localCharityList, setLocalCharityList] = useState();
    const [partnershipsDataFull, setPartnershipsDataFull] = useState(null);
    const [pageCount, setPageCount] = useState();

    const getData = useCallback(() => {
        setIsLoading(true);

        getAllPartnerships(mode)
            .then((res) => {
                let partnershipsArray = [];
                res.data._embedded.partnership.map((partnership) =>
                    partnershipsArray.push(
                        partnership.association.idAssociation,
                    ),
                );

                if (isMounted.current) {
                    setPartnershipsData(partnershipsArray);
                    setPartnershipsDataFull(res.data._embedded.partnership);
                }
            })
            .catch((error) => {
                addError(
                    error?.response?.data.detail,
                    error?.response?.data.status,
                );
            })
            .finally(() => {
                isMounted.current && setIsLoading(false);
            });
    }, [addError, mode]);

    const getCharityListData = useCallback(
        ({ pageIndex }) => {
            const params = {
                limit: 12,
                page: mode === MODES.PROD ? pageIndex + 1 : 1,
            };

            setIsListLoading(true);

            getAllCharities(params, mode)
                .then((res) => {
                    const {
                        page_count,
                        _embedded: { association },
                    } = res.data;

                    if (isMounted.current) {
                        setPageCount(page_count);

                        setLocalCharityList(association);
                    }
                })
                .catch((error) => {
                    const { detail, status } = error?.response?.data;

                    addError(detail, status);
                })
                .finally(() => {
                    isMounted.current && setIsListLoading(false);
                });
        },
        [addError, mode],
    );

    useEffect(() => {
        isMounted.current = true;

        getData();

        return () => {
            isMounted.current = false;
        };
    }, [getData, mode, localCharityList]);

    const getOnePartnershipData = (id) => {
        return (
            localCharityList &&
            partnershipsDataFull?.find(
                (item) => item.association.idAssociation === id,
            )
        );
    };

    return (
        <>
            <Helmet>
                <title>
                    {t('ShopPartnerships.allCharities.metaTitle')} -{' '}
                    {t('main.metaTitle')}
                </title>
            </Helmet>

            <H1Title title={t('ShopPartnerships.allCharities.title')}>
                <ToggleMode />
            </H1Title>

            <Introduction>
                {t('ShopPartnerships.allCharities.introduction')}
            </Introduction>

            <div>
                {isLoading || isListLoading ? (
                    <PartnershipCardSkeleton blockWidth="md" repeat={12} />
                ) : localCharityList?.length > 0 ? (
                    <PartnershipCardGrid>
                        {partnershipsDataFull !== null &&
                            localCharityList.map((charity) => {
                                const { idAssociation, name } = charity;

                                return (
                                    <ErrorBoundary
                                        blockWidth="md"
                                        key={idAssociation}
                                    >
                                        <PartnershipCollapsibleCard
                                            key={idAssociation}
                                            data={
                                                partnershipsData?.includes(
                                                    idAssociation,
                                                )
                                                    ? partnershipsDataFull &&
                                                      getOnePartnershipData(
                                                          idAssociation,
                                                      )
                                                    : charity
                                            }
                                            mainButton={
                                                !partnershipsData?.includes(
                                                    idAssociation,
                                                ) ? (
                                                    <Disabler>
                                                        <CardMainButton
                                                            getData={getData}
                                                            partnerId={
                                                                idAssociation
                                                            }
                                                            partnerName={name}
                                                        />
                                                    </Disabler>
                                                ) : null
                                            }
                                            partnershipStatus={
                                                partnershipsData?.includes(
                                                    idAssociation,
                                                )
                                                    ? partnershipsDataFull &&
                                                      getPartnershipStatus(
                                                          getOnePartnershipData(
                                                              idAssociation,
                                                          ),
                                                      )
                                                    : null
                                            }
                                            isAlreadyPartner={partnershipsData?.includes(
                                                idAssociation,
                                            )}
                                        />
                                    </ErrorBoundary>
                                );
                            })}
                    </PartnershipCardGrid>
                ) : (
                    <NoDataFoundCard
                        message={t('ShopPartnerships.allCharities.noDataFound')}
                    />
                )}

                <AllCharitiesPagination
                    getCharityListData={getCharityListData}
                    charityListData={localCharityList}
                    pageCount={pageCount}
                />
            </div>
        </>
    );
};

export default withTranslation('common')(AllCharities);
